import { addSerialNumberToCsv } from "../lib/csv";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Download,
  Search,
  ChevronUp,
  ChevronDown,
} from "lucide-react";
import ERPAnalyticsHeader from "../components/ERPAnalyticsHeader";
import { api } from "../lib/api";
import { formatDateValue } from "../lib/dateFormat";

const STATUS_OPTIONS = [
  { value: "all", label: "All" },
  { value: "released", label: "Released" },
  { value: "hold", label: "Hold" },
  { value: "pending", label: "Pending" },
];

const PAGE_SIZE_OPTIONS = [25, 50, 100, 250, 500];

function safeDate(value) {
  if (!value) return "—";
  return formatDateValue(value);
}

function csvEscape(value) {
  const s = value === null || value === undefined ? "" : String(value);
  return `"${s.replace(/"/g, '""')}"`;
}

function badgeClass(status) {
  if (status === "Released") {
    return "bg-emerald-50 text-emerald-700 border-emerald-200";
  }
  if (status === "Hold") {
    return "bg-red-50 text-red-700 border-red-200";
  }
  return "bg-amber-50 text-amber-700 border-amber-200";
}

export default function FdaReleaseHoldDetail() {
  const nav = useNavigate();
  const location = useLocation();

  const params = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );

  const fromDate = params.get("from") || "";
  const toDate = params.get("to") || "";
  const initialStatus = params.get("status") || "all";

  const [status, setStatus] = useState(initialStatus);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [meta, setMeta] = useState(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [search, setSearch] = useState("");
  const [pageSize, setPageSize] = useState(50);
  const [page, setPage] = useState(1);

  const [sort, setSort] = useState({
    field: "CInvDate",
    direction: "desc",
  });

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        setLoading(true);
        setError("");

        const response = await api.post(
          "/ai/ceo-chat/fda-release-hold/detail",
          {
            from_date: fromDate || undefined,
            to_date: toDate || undefined,
            status,
          },
          { timeout: 60000 }
        );

        const data = response?.data ?? response;

        if (!cancelled) {
          setRows(Array.isArray(data?.rows) ? data.rows : []);
          setSummary(data?.summary || null);
          setMeta(data || null);
        }
      } catch (e) {
        if (!cancelled) {
          setRows([]);
          setSummary(null);
          setMeta(null);
          setError(
            e?.response?.data?.detail ||
              e?.message ||
              "FDA Release / Hold details could not be loaded safely."
          );
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [fromDate, toDate, status]);

  useEffect(() => {
    setPage(1);
  }, [search, status, pageSize, sort]);

  const searchedRows = useMemo(() => {
    const q = search.trim().toLowerCase();

    if (!q) return rows;

    return rows.filter((row) =>
      Object.values(row || {}).some((value) =>
        String(value ?? "").toLowerCase().includes(q)
      )
    );
  }, [rows, search]);

  const sortedRows = useMemo(() => {
    const copy = [...searchedRows];

    if (!sort.field) return copy;

    copy.sort((a, b) => {
      const result = String(a?.[sort.field] ?? "").localeCompare(
        String(b?.[sort.field] ?? ""),
        undefined,
        { numeric: true }
      );

      return sort.direction === "desc" ? -result : result;
    });

    return copy;
  }, [searchedRows, sort]);

  const totalPages = Math.max(1, Math.ceil(sortedRows.length / pageSize));
  const currentPage = Math.min(page, totalPages);

  const pagedRows = sortedRows.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  const changeSort = (field) => {
    setSort((current) => ({
      field,
      direction:
        current.field === field && current.direction === "asc"
          ? "desc"
          : "asc",
    }));
  };

  const applyStatus = (value) => {
    setStatus(value);

    const next = new URLSearchParams(location.search);
    next.set("scope", "fda");
    next.set("status", value);

    nav(`/ceo-command-center/fda-release-hold?${next.toString()}`, {
      replace: true,
    });
  };

  const exportCsv = () => {
    if (!sortedRows.length) return;

    const columns = [
      "PoNo",
      "PoDate",
      "CustomerName",
      "BuyerName",
      "DestinationCountry",
      "CInvNo",
      "CInvDate",
      "ContainerNo",
      "ShippingBillNo",
      "FDAStatus",
      "FDAEUHoldDate",
      "FDAEUReleaseCFIADate",
      "FDAEntryNumber",
      "FDAEntryDate",
      "FDAExam",
      "ERPWorkflowStatus",
      "FDAContainerStatus",
      "FDARemarks",
    ];

    const csv = [
      columns.map(csvEscape).join(","),
      ...sortedRows.map((row) =>
        columns.map((column) => csvEscape(row?.[column])).join(",")
      ),
    ].join("\n");

    const blob = new Blob([addSerialNumberToCsv(csv)], {
      type: "text/csv;charset=utf-8;",
    });

    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");

    a.href = url;
    a.download = `fda-release-hold-${status}.csv`;

    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const columns = [
    ["PoNo", "PO Number"],
    ["CustomerName", "Customer / Consignee"],
    ["CInvNo", "Commercial Invoice"],
    ["CInvDate", "Invoice Date"],
    ["ContainerNo", "Container"],
    ["FDAStatus", "CEO FDA Status"],
    ["ERPWorkflowStatus", "ERP Workflow Status"],
    ["FDAEUHoldDate", "FDA Hold Date"],
    ["FDAEUReleaseCFIADate", "FDA Release Date"],
    ["FDAEntryNumber", "FDA Entry Number"],
    ["FDAEntryDate", "FDA Entry Date"],
    ["FDAExam", "FDA Exam"],
    ["ShippingBillNo", "Shipping Bill"],
    ["DestinationCountry", "Country"],
  ];

  const SortIcon = ({ field }) => {
    if (sort.field !== field) return null;

    return sort.direction === "asc"
      ? <ChevronUp size={14} />
      : <ChevronDown size={14} />;
  };

  return (
    <div className="min-h-screen" style={{ background: "var(--c-bg)" }}>
      <ERPAnalyticsHeader />

      <main className="px-4 md:px-6 py-5 max-w-[1800px] mx-auto">
        <div className="flex flex-col gap-4">
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
            <div>
              <button
                onClick={() => nav("/ceo-command-center")}
                className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900 mb-3"
              >
                <ArrowLeft size={16} />
                Back to CEO Command Center
              </button>

              <h1 className="text-2xl font-semibold text-slate-900">
                FDA Release / Hold
              </h1>

              <p className="text-sm text-slate-500 mt-1">
                FDA regulatory dates with Pending aligned to the ERP FDA Status workflow.
              </p>

              <div className="text-xs text-slate-500 mt-2">
                Period: {formatDateValue(meta?.date_range?.from_date || fromDate) || "Current FY"} →{" "}
                {formatDateValue(meta?.date_range?.to_date || toDate) || "Current FY"}
              </div>
            </div>

            <button
              onClick={exportCsv}
              disabled={!sortedRows.length}
              className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg border border-slate-200 bg-white text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-40"
            >
              <Download size={16} />
              CSV
            </button>
          </div>

          {summary && (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <button
                onClick={() => applyStatus("all")}
                className="text-left rounded-xl border border-slate-200 bg-white p-4"
              >
                <div className="text-xs uppercase tracking-wide text-slate-500">
                  Eligible POs
                </div>
                <div className="text-2xl font-semibold mt-1">
                  {summary.total_count ?? 0}
                </div>
              </button>

              <button
                onClick={() => applyStatus("released")}
                className="text-left rounded-xl border border-emerald-200 bg-white p-4"
              >
                <div className="text-xs uppercase tracking-wide text-emerald-700">
                  Released
                </div>
                <div className="text-2xl font-semibold mt-1 text-emerald-700">
                  {summary.released_count ?? 0}
                </div>
              </button>

              <button
                onClick={() => applyStatus("hold")}
                className="text-left rounded-xl border border-red-200 bg-white p-4"
              >
                <div className="text-xs uppercase tracking-wide text-red-700">
                  Hold
                </div>
                <div className="text-2xl font-semibold mt-1 text-red-700">
                  {summary.hold_count ?? 0}
                </div>
              </button>

              <button
                onClick={() => applyStatus("pending")}
                className="text-left rounded-xl border border-amber-200 bg-white p-4"
              >
                <div className="text-xs uppercase tracking-wide text-amber-700">
                  Pending
                </div>
                <div className="text-2xl font-semibold mt-1 text-amber-700">
                  {summary.pending_count ?? 0}
                </div>
              </button>
            </div>
          )}

          <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
            <div className="p-4 border-b border-slate-200 flex flex-col xl:flex-row xl:items-center xl:justify-between gap-3">
              <div className="flex flex-wrap gap-2">
                {STATUS_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => applyStatus(option.value)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium border ${
                      status === option.value
                        ? "bg-slate-900 text-white border-slate-900"
                        : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>

              <div className="flex items-center gap-2">
                <div className="relative">
                  <Search
                    size={15}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                  />
                  <input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search FDA records..."
                    className="w-[260px] max-w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-slate-200"
                  />
                </div>

                <select
                  value={pageSize}
                  onChange={(e) => setPageSize(Number(e.target.value))}
                  className="border border-slate-200 rounded-lg px-3 py-2 text-sm"
                >
                  {PAGE_SIZE_OPTIONS.map((size) => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {error ? (
              <div className="p-6 text-sm text-red-700 bg-red-50">
                {error}
              </div>
            ) : loading ? (
              <div className="p-10 text-center text-sm text-slate-500">
                Loading FDA Release / Hold records...
              </div>
            ) : (
              <>
                <div className="overflow-auto">
                  <table className="min-w-[1900px] w-full text-sm">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr>
                        {columns.map(([field, label]) => (
                          <th
                            key={field}
                            onClick={() => changeSort(field)}
                            className="text-left px-4 py-3 font-semibold text-slate-600 cursor-pointer whitespace-nowrap"
                          >
                            <span className="inline-flex items-center gap-1">
                              {label}
                              <SortIcon field={field} />
                            </span>
                          </th>
                        ))}
                      </tr>
                    </thead>

                    <tbody>
                      {pagedRows.map((row, index) => (
                        <tr
                          key={`${row.PoID}-${index}`}
                          className="border-b border-slate-100 hover:bg-slate-50"
                        >
                          {columns.map(([field]) => (
                            <td
                              key={field}
                              className="px-4 py-3 text-slate-700 align-top whitespace-nowrap"
                            >
                              {field === "FDAStatus" ? (
                                <span
                                  className={`inline-flex px-2 py-1 rounded-md border text-xs font-medium ${badgeClass(
                                    row[field]
                                  )}`}
                                >
                                  {row[field] || "Pending"}
                                </span>
                              ) : field.toLowerCase().includes("date") ? (
                                safeDate(row[field])
                              ) : (
                                row[field] || "—"
                              )}
                            </td>
                          ))}
                        </tr>
                      ))}

                      {!pagedRows.length && (
                        <tr>
                          <td
                            colSpan={columns.length}
                            className="px-4 py-10 text-center text-slate-500"
                          >
                            No FDA records found for this filter.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                <div className="px-4 py-3 border-t border-slate-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 text-sm">
                  <div className="text-slate-500">
                    {sortedRows.length} records
                    {meta?.truncated ? " · result capped at 5,000 rows" : ""}
                    {meta?.reconciliation?.passed ? " · reconciled" : ""}
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      disabled={currentPage <= 1}
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      className="px-3 py-1.5 rounded-lg border border-slate-200 disabled:opacity-40"
                    >
                      Previous
                    </button>

                    <span className="text-slate-600">
                      Page {currentPage} of {totalPages}
                    </span>

                    <button
                      disabled={currentPage >= totalPages}
                      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                      className="px-3 py-1.5 rounded-lg border border-slate-200 disabled:opacity-40"
                    >
                      Next
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>

        </div>
      </main>
    </div>
  );
}
